import { type ComponentRef, useState, type ReactElement, type ReactNode } from 'react'
import type { SerializedDiamond } from '../types'
import { Button, CardBody, Card, Popover, PopoverBody } from '@stuller/stullercom/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fas } from '@awesome.me/kit-3dbd93c064/icons'
import { SerializedDiamondDetailsTable } from './SerializedDiamondDetailsTable'
import { isStringEmpty } from '@stuller/shared/util/core'
import Link from 'next/link'

interface SerializedDiamondDetailsProps {
  /**
   * The serialized diamond to display
   */
  serializedDiamond: SerializedDiamond
}

interface DetailCardProps {
  value: ReactNode | string
  label: string
}

/**
 * Card component for displaying diamond details
 */
function DetailCard ({ value, label }: DetailCardProps): ReactElement {
  return (
    <Card className='bg-gray-100 text-center border-0'>
      <CardBody className='px-0'>
        <div className='h3 mb-0 fw-normal'>{value}</div>
        <div className='h6 mb-0 fw-normal'>{label}</div>
      </CardBody>
    </Card>
  )
}

/**
 * Card component for displaying GCal8x diamond details with popover
 */
function GCal8xDetailCard (): ReactElement {
  const [open, handleOpenChange] = useState(false)
  const [cardRef, setCardRef] = useState<ComponentRef<typeof Card> | null>(null)

  return (
    <>
      <Card ref={setCardRef} className='bg-gray-100 text-center border-0'>
        <CardBody>
          <div style={{ maxHeight: '26px' }}>
            <img src='https://assets.stullercloud.com/das/103958331.svg' style={{ maxHeight: '30px' }} />
          </div>
          <div className='h6 mb-0 fw-normal'>Make</div>
        </CardBody>
      </Card>
      <Popover open={open} onOpenChange={handleOpenChange} reference={cardRef} placement='top' hover>
        <PopoverBody>
          Diamonds that achieve excellent (Ex) grades in all 8 aspects of cut quality assessment: proportions, external symmetry, polish, optical brilliance, fire, scintillation, optical symmetry, and hearts & arrows.
        </PopoverBody>
      </Popover>
    </>
  )
}

function SerializedDiamondDetails ({ serializedDiamond }: SerializedDiamondDetailsProps): ReactElement {
  return (
    <div className='row gx-2'>
      {serializedDiamond.isGCal8x && (
        <div className='mb-3 d-flex flex-row flex-lg-column flex-xl-row gap-3'>
          <div>
            <img src='https://meteor.stullercloud.com/das/107024980?wid=153' />
          </div>
          <span className='align-self-center'>
            Diamonds that achieve an Excellent grade in all eight aspects of Cut quality assessment.&thinsp;
            <Link href='/8XLGDiamondsInfo' target='__blank' className='text-nowrap'>Learn More</Link>
          </span>
        </div>
      )}
      {serializedDiamond.isCanadaMark === true && (
        <div className='mb-3 d-flex flex-row flex-lg-column flex-xl-row gap-3'>
          <div>
            <img src='http://meteor.stullercloud.com/das/13599292?wid=153' />
          </div>
          <span className='align-self-center'>
            Responsibly sourced from Canada with 100% traceable origin.&thinsp;
            <Link href='/canadiandiamondsinfo' target='__blank' className='text-nowrap'>Learn More</Link>
          </span>
        </div>
      )}
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        <DetailCard value={serializedDiamond.weight} label='Carat' />
      </div>
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        <DetailCard value={serializedDiamond.color} label='Color' />
      </div>
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        <DetailCard value={serializedDiamond.clarity} label='Clarity' />
      </div>
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        {serializedDiamond.isGCal8x
          ? (
            <GCal8xDetailCard />
          )
          : (
            <DetailCard value={serializedDiamond.makeLookup} label='Make' />
          )}
      </div>
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        <DetailCard value={serializedDiamond.polishLookup} label='Polish' />
      </div>
      <div className='col-4 col-sm-2 col-lg-4 col-xl-2 mb-2'>
        <DetailCard value={serializedDiamond.symmetryLookup} label='Symmetry' />
      </div>
      {!isStringEmpty(serializedDiamond.report?.url) && (
        <div className='mb-4'>
          <Card className='bg-gray-100 text-center border-0'>
            <CardBody className='px-0 py-4'>
              <Button href={serializedDiamond.report?.url} target='_blank' tag='a'>
                <FontAwesomeIcon icon={fas.faFile} className='me-2' />
                {serializedDiamond.report?.provider} Report
              </Button>
            </CardBody>
          </Card>
        </div>
      )}
      <div>
        <SerializedDiamondDetailsTable serializedDiamond={serializedDiamond} />
      </div>
    </div>
  )
}

export {
  SerializedDiamondDetails
}
